// "The product": what a Collect workspace is (the real layout `collect init` creates,
// apps/cli/collect.py Workspace.init) and who Collect is for (docs/01-OVERVIEW.md "For",
// docs/spec/COLLECT-MVP-SPEC.md §3 personas — no names).
import { motion } from "motion/react";
import { BookOpenText, Bot, ShieldCheck } from "lucide-react";
import { useLandingTheme } from "./theme";

const TREE: Array<[string, string]> = [
  ["purpose.md", "what this wiki is for — read on every ingest"],
  ["sources/", "your documents, as Markdown"],
  ["summaries/", "one per source, citing it"],
  ["concepts/", "pages the LLM creates and updates"],
  ["kg/triples.json", "subject · predicate · object"],
  ["index.md", "rebuilt after every ingest"],
  ["log.md", "append-only ledger"],
];

const FOR = [
  {
    icon: BookOpenText,
    title: "People who read and write for a living",
    desc: "Keep notes, articles and documents as plain Markdown with its own git history — readable without Collect, not locked in someone else's cloud.",
  },
  {
    icon: Bot,
    title: "AI builders",
    desc: "A scriptable ingest pipeline (CLI + HTTP API), a knowledge graph of triples for grounded prompts, and your own Anthropic, OpenAI or OpenRouter key.",
  },
  {
    icon: ShieldCheck,
    title: "Archives that must hold up",
    desc: "Every capsule carries a SHA-256 hash, checked again when it is opened. Sealing a workspace with post-quantum encryption is experimental, in the CLI only.",
  },
];

export function ProductSection() {
  const { tone } = useLandingTheme();
  return (
    <section id="product" aria-labelledby="product-title" className="px-6 md:px-12 py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16 items-start">
          <header>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-4">The product</p>
            <h2 id="product-title" className="text-5xl md:text-7xl font-serif font-light leading-[0.95] mb-8">
              Your knowledge,<br />
              <span className="italic text-white/65">in files you own.</span>
            </h2>
            <p className="text-base md:text-lg font-light text-white/70 leading-relaxed max-w-xl">
              A Collect workspace is a folder of Markdown under git. You add sources; the LLM
              writes the summaries, concept pages and knowledge graph around them. When a
              workspace is ready to keep or share, Collect packs it into one capsule and sends it
              to your HIVE, where the web app lists it, opens it and compares versions.
            </p>
          </header>

          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-panel-strong rounded-2xl p-6 md:p-8 min-w-0"
          >
            <figcaption className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mb-5">
              collect init my-wiki
            </figcaption>
            <p className="font-mono text-sm mb-3" style={{ color: tone("#34D399") }}>my-wiki/</p>
            <dl className="font-mono text-[13px] grid gap-2.5">
              {TREE.map(([path, note]) => (
                <div key={path} className="grid grid-cols-1 sm:grid-cols-[10.5rem_1fr] gap-x-4 gap-y-0.5 pl-4 border-l border-white/10">
                  <dt className="text-white/80 break-all">{path}</dt>
                  <dd className="m-0 text-white/60 font-sans text-[13px] font-light">{note}</dd>
                </div>
              ))}
            </dl>
          </motion.figure>
        </div>

        <h3 className="mt-20 md:mt-24 mb-8 font-mono text-xs uppercase tracking-[0.3em] text-white/60">Who it is for</h3>
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FOR.map((f, idx) => {
            const Icon = f.icon;
            return (
              <motion.li
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-panel rounded-2xl p-7 md:p-8"
              >
                <Icon size={22} strokeWidth={1.4} className="mb-6" style={{ color: tone("#34D399") }} aria-hidden="true" />
                <h4 className="text-2xl font-serif font-light mb-3">{f.title}</h4>
                <p className="text-sm font-light text-white/70 leading-relaxed">{f.desc}</p>
              </motion.li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
